/**
 * 48コマ編集表のカード (枠 + タイトル + 保存ボタン + KomaGrid)。
 * index.tsx から presentational 部分を切り出したコンポーネント。
 *
 * 明示保存モデル (設計書 demand-forecast-explicit-save §4/§5):
 * - 「保存」ボタン (design `dft-table-save` 準拠、AC-038/039/040): dirty
 *   (未保存の変更あり) のときのみ活性。押下で dirty な全小売分を保存する。
 * - 保存状態: 「保存中…」(loading) / 「最終保存 M/D HH:mm」/ 保存失敗エラー
 *   (AC-042: 未保存を保持したまま再試行可能) / 楽観ロック衝突 (AC-09: 衝突
 *   メッセージ + 再読込導線、自動 refetch なし)。
 * - 旧自動保存インジケータ (保存中…/自動保存エラー/衝突通知の per-row 表示) は
 *   撤去し、上記の明示保存状態表示に一本化した (AC-14)。
 *
 * spec 準拠改修 (demand-forecast-daily-v4、AC-01) で以下を削除した:
 * MWh/kWh 単位切替・過去実績「採用」ボタン・採用中バナー。リセットボタンは
 * 当初 AC-01 の一部として削除したが、共通仕様 demand-forecast-common.md
 * AC-DFC-EDIT-002 (SHALL) の見落としと判明し復元済み (spec 準拠)。プロバイダ
 * 予測列は spec 準拠調整 (demand-forecast-daily-spec-align, AC-08) で削除した。
 * 「取込元」サブラベルは同改修で一時削除されたが、demand-48koma-columns
 * (AC-01/AC-02) で design 準拠の条件付き描画として復活させた (下記 sourceLabel)。
 *
 * BG 全体ビューの直接編集 (spec AC-004c/AC-004d、demand-forecast-daily-
 * spec-align):
 * - AC-004c: 配下小売が 1 件のみのときの isReadOnly 解除 (合算値=内訳のため
 *   セル編集を許可) は page-model の gate (`forecastGates.isReadOnly`) が合成し、
 *   本コンポーネントは結果を `view.editing.isReadOnly` から消費するだけ
 *   (再計算しない、slice 2 で個別 prop から view field へ移設)。
 * - AC-004d: 配下小売が複数で未入力の内訳がある (aggregateUnfilledRetailerNames)
 *   かつ編集可 (canEditNow) のときは テーブル上部にインフォメッセージを表示する
 *   (全内訳に値があれば集計表示から編集できる、AC-DFD-054)。
 *   かつて description スロットに「小売電気事業者を選択して編集する」TextButton を
 *   置き、押下で配下小売一覧の先頭を選んで RETAILER scope へ切り替えていたが、
 *   **遷移先が一意に定まらず「どの小売に飛ぶか分からない」** ため 2026-08-25 の
 *   PdM 判断で導線ごと撤去した (issue #6302、spec AC-DFD-004d の ※)。
 *   対象切替は画面上部の BG/小売電気事業者/需要グループ切替 (AC-DFC-001) に一本化する。
 *
 * コマ編集は onChange で親に委譲 (状態は親が保持)。
 */
import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { formatMonthDayTime } from '~/models/dateTime'
import { BgExpiredNoticeView } from '~/pages/DemandForecastDaily/components/BgExpiredNoticeView'
import { ConflictReloadConfirmDialog } from '~/pages/DemandForecastDaily/components/ConflictReloadConfirmDialog'
import { DiscardConfirmModal } from '~/pages/DemandForecastDaily/components/DiscardConfirmModal'
import { KomaGrid } from '~/pages/DemandForecastDaily/components/KomaGrid'
import { useStickyBarHeight } from '~/pages/DemandForecastDaily/hooks/useKomaTheadSticky'
import { formatBreakdownNames } from '~/pages/DemandForecastDaily/models/aggregateEdit'
import type {
  ForecastReferenceColumnValueKind,
  ForecastSectionView,
} from '~/pages/DemandForecastDaily/models/forecastSectionView'
import type { AdjustmentDisabledReasonKey } from '~/pages/DemandForecastDaily/models/toolbarGuards'
import type {
  KomaSlots,
  SaveState,
} from '~/pages/DemandForecastDaily/models/types'

/**
 * `toolbarGuards.adjustmentDisabledReasonKey` の理由キー → i18n key
 * (参照列「採用」ボタンの disabled tooltip 文言、AC-DFD-047b/048a)。理由キーの
 * 基底は page-model の gate (`forecastGates.adjustmentDisabledReasonKey`、Toolbar の
 * 「調整」ボタンと同一) を prop で受け取り、本コンポーネントは文言のみ「採用」向けに
 * 差し替える (優先: 過去日 > 確定済み > 小売未選択)。ただし集計スコープ
 * (BG全体×配下複数) は確定履歴の per-retailer 採用が小売選択を要件にしないため
 * (design note tmp/designing-feature/demand-fix-aggregate-reference-adopt.md
 * Decision 2/6) noRetailer は抑制する (下記 adoptDisabledReasonKey 算出箇所参照。
 * 過去実績列は集計スコープで KomaGrid が非表示にするため本値が効くのは確定履歴列のみ)。
 */
const ADOPT_DISABLED_REASON_I18N_KEYS: Record<
  AdjustmentDisabledReasonKey,
  string
> = {
  pastDate: 'page:demandForecastDaily.koma.adoptDisabledPastDate',
  completed: 'page:demandForecastDaily.koma.adoptDisabledCompleted',
  allGcLocked: 'page:demandForecastDaily.koma.adoptDisabledAllGcLocked',
  unattributedRemaining:
    'page:demandForecastDaily.confirm.serverError.reason.unattributedRemaining',
  noRetailer: 'page:demandForecastDaily.koma.adoptDisabledNoRetailer',
  groupTotal: 'page:demandForecastDaily.breakdown.groupTotalNotice',
  groupForecastBg: 'page:demandForecastDaily.breakdown.groupForecastBgNotice',
}

type Props = {
  /**
   * 断面の描画専用値型 (D9、slice 2)。48コマ表向けの gate 済み field
   * (displaySlots / baselineSlots / todayActualSlots / gcKoma / currentKoma /
   * pastActualSlots / adoptedReferenceColumnKey / dirtyKomas / isReadOnly /
   * isRetailerEditableTarget の否定 / isReferenceView) は
   * すべてここから読む (`buildForecastSectionView`、index.tsx が useMemo で
   * 1 回だけ呼ぶ)。sourceLabel / adoptDisabledReason (i18n 文言) と
   * headBarHeight (実測) は presentation のため view に入れず本コンポーネントが
   * 導出し続ける (D8/D9)。KomaGrid へは同じ view インスタンスをそのまま転送する。
   */
  view: ForecastSectionView
  /** タイトル兼小売名 (BG全体時は専用タイトルを使う) */
  displayLabel: string
  /**
   * 「今この断面が値変更を許すか」の時相 gate (page-model の
   * `forecastGates.canEditNow`)。保存ボタン / BG 複数小売インフォメッセージの
   * 活性判定に使う。書込禁止 gate のため required (fail-open 防止)。
   */
  canEditNow: boolean
  /**
   * 「調整」/「採用」disabled 理由キー (page-model の
   * `forecastGates.adjustmentDisabledReasonKey`、優先: 過去日 > 確定済み > 小売未選択)。
   * KomaCard 側で 4 入力を再組立せず gate 導出値をそのまま消費する。
   * 参照列「採用」の disabled を左右する書込禁止 gate のため、isReadOnly / canEditNow と
   * 同様 required にする (undefined = 活性は有効値。prop 欠落で確定済み断面でも採用が
   * 押せる fail-open を型で塞ぐ。PR #6276 review OzawaShigenori)。
   */
  adjustmentDisabledReasonKey: AdjustmentDisabledReasonKey | undefined
  /**
   * グループ別の小売のために参照列の「採用」を止めている理由 (AC-8)。指定時は他の理由より
   * 優先して採用を押せなくする。
   */
  adoptBlockedReason?: string
  /** コマ編集 (単一セル手入力) */
  onKomaChange: (
    koma: number,
    value: number | undefined,
    nextSlots: KomaSlots,
  ) => void
  /**
   * 貼付 (spec AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ上書き」)。
   * onKomaChange (単一セル編集、比例配分の対象) とは別経路 (demand-fix-edit-operation)。
   */
  onUndoAggregate?: () => void
  inputParser?: (raw: string) => number | undefined
  onPaste?: (startKoma: number, nextSlots: KomaSlots) => void
  /**
   * AC-DFD-047/048: 参照列ヘッダー「採用」ボタン押下 (押下中の再クリックは
   * `useForecastDraftEditor` 側で解除〈AC-DFD-047b〉と判定する)。
   */
  onAdoptReferenceColumn?: (column: {
    key: string
    sourceLabel: string
    slots: KomaSlots
    valueKind: ForecastReferenceColumnValueKind
  }) => void
  /** AC-15: 列 (時間帯) ホバー時に呼び出し元へ通知する */
  onHoverKoma?: (koma: number | undefined) => void
  /** AC-DFD-006a-6: 参照列見出しのホバー時に、チャートで強調する系列を呼び出し元へ通知する */
  onHoverSeriesKey?: (seriesKey: string | undefined) => void
  /** 変更破棄確定時コールバック (共通仕様 AC-DFC-EDIT-002、確認モーダルを経て呼ばれる) */
  onDiscard?: () => void
  /**
   * BG 全体ビュー (配下複数) で未入力の内訳名 (spec AC-DFD-004d、page-model の
   * gate が導出)。1 件以上の時は編集・保存できない理由として
   * 「{内訳名}が未入力のため編集できません」を表示し、保存ボタンを非活性にする。
   */
  aggregateSaveLocked?: boolean
  aggregateUnfilledRetailerNames?: string[]
  /**
   * 共通仕様 AC-DFC-EDIT-002「変更を破棄」ボタンの活性条件。dirty 判定 (値差 or
   * 取込元メタ差) を含め page-model の gate (`forecastGates.canDiscard`) が単一 seam
   * (`useForecastDirtyState` の currentRowDirty = `isRowDirty`) から導出済みの値を
   * 渡す。KomaCard 側で `computeForecastDiff` を再計算しない (dirty 導出を 1 seam
   * に統一するアーキ改善)。
   */
  canDiscard: boolean
  /** AC-038/039: 画面全体で 1 つでも dirty な小売があるか (保存ボタン活性の判定源)。 */
  anyDirty: boolean
  /** 保存 RPC 実行中か (保存ボタンの loading 表示・多重送信防止)。 */
  isPreviewPending?: boolean
  isSaving: boolean
  /** 最終保存時刻 (表示用)。undefined = まだ保存していない。 */
  lastSavedAt?: Date
  /** 選択中小売の保存状態 (エラー/衝突メッセージの表示判定に使う)。 */
  currentSaveState?: SaveState
  /**
   * 選択中小売の保存失敗原因別文言 (currentSaveState==='error' の時のみ表示)。
   * 未解決 (undefined) の場合は既存の code ベース fallback 文言
   * (`page:demandForecastDaily.save.error`) を表示する。
   */
  saveErrorMessage?: string
  /** 「保存」ボタン押下 (AC-038/039/040)。 */
  onSave: () => void
  /** 楽観ロック衝突 (AC-09) の「最新を読み込み直す」導線。 */
  onResolveConflict?: () => void
  /**
   * AC-DFC-002a (design note tmp/designing-feature/demand-bg-expired-
   * message.md): ケース自身の需要 BG が対象日時点で失効し有効小売が 0 件の
   * とき、テーブル本体領域 (本コンポーネントの描画全体) を BG 失効の案内表示に
   * 差し替える。present のときは他の全 prop を無視して案内のみ描画する
   * (小売の行=KomaGrid を出さない)。SectionHeader は呼び出し元 (index.tsx) が
   * 別途維持する。
   */
  bgExpiredNotice?: {
    targetDateLabel: string
  }
}

export const KomaCard = ({
  view,
  displayLabel: _displayLabel,
  canEditNow,
  adjustmentDisabledReasonKey,
  adoptBlockedReason,
  onKomaChange,
  onPaste,
  inputParser,
  onUndoAggregate,
  onAdoptReferenceColumn,
  onHoverKoma,
  onHoverSeriesKey,
  onDiscard,
  aggregateUnfilledRetailerNames = [],
  aggregateSaveLocked,
  anyDirty,
  canDiscard,
  isSaving,
  isPreviewPending,
  lastSavedAt,
  currentSaveState,
  saveErrorMessage,
  onSave,
  onResolveConflict,
  bgExpiredNotice,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)
  const [isConflictReloadConfirmOpen, setIsConflictReloadConfirmOpen] =
    useState(false)
  // 48コマ表の列ヘッダー行が貼り付く位置 = この下の見出しバーの実測高。
  // 早期 return より前に呼ぶ (Rules of Hooks)。
  const { ref: headBarRef, height: headBarHeight } = useStickyBarHeight()

  // AC-DFC-002a: BG 失効の案内表示中はテーブル本体領域全体 (本コンポーネントの
  // 通常描画: ヘッダー行・保存ボタン・KomaGrid) を空状態 view に差し替える
  // (小売の行を出さない)。SectionHeader は呼び出し元が維持するためここでは
  // 触らない。他の全 gate/prop より優先する早期 return (hooks は上の useState
  // 2 つと useStickyBarHeight のみで以降に無いため Rules of Hooks 上安全)。
  if (bgExpiredNotice !== undefined) {
    return (
      <BgExpiredNoticeView targetDateLabel={bgExpiredNotice.targetDateLabel} />
    )
  }

  // Decision 6 (design note tmp/designing-feature/demand-fix-aggregate-
  // reference-adopt.md): 集計スコープ判定 (= 配下複数の集計表示) の SoT は
  // page-model の gate が導出する isRetailerEditableTarget の否定。builder が
  // 1 箇所で計算した値 (view.editing.isAggregateScope、D7) を消費するだけで
  // targetScope / isSingleRetailerBg から再計算しない (編集可否決定を gate に
  // 一本化するアーキ改善。BG_TOTAL リテラル分岐を増やさない)。
  const isAggregateScope = view.editing.isAggregateScope

  // 「変更を破棄」の活性条件 (canDiscard) は page-model の gate
  // (`forecastGates.canDiscard`) が単一 seam (`useForecastDirtyState` の
  // currentRowDirty = `isRowDirty`) から導出済みで、prop として受け取る。dirty
  // 判定 (値差 or 取込元メタ差) を KomaCard 内で再計算しない (アーキ改善: dirty
  // 導出を 1 seam に統一)。過去日 gate を含めない理由・破棄経路を残す理由は
  // gate 側 (forecastGates.ts) のコメント参照。

  // isReadOnly (KomaGrid セル / チャート編集の可否基底) は page-model の
  // `forecastGates.isReadOnly` を view.editing.isReadOnly 経由で読む (AC-004c の
  // BG 配下 1 件解除 / 集計スコープは未入力の内訳がある時だけ read-only〈AC-DFD-004d /
  // 054〉/ conventions.md §GC ロックの過去日ロック / 確定済みロックを gate 側で合成済み)。KomaCard 側での raw 再計算は
  // やめて gate の単一 SoT を消費する。

  // AC-004d: BG 全体ビュー + 配下小売が複数で未入力の内訳がある + 編集可 (canEditNow)
  // のときのみ、直接編集・保存できない理由 (未入力の内訳名) と導線 (小売選択) を
  // インフォメッセージで案内する。全内訳に値があれば集計表示から編集できる (AC-DFD-054)。
  // 過去日 / 確定済みは値変更不可のため案内自体を出さない (conventions.md §GC ロック
  // L239「GC ロック中の案内文には…変更手段が存在するかのような文言を使用しない」
  // 準拠。小売切替後も canEditNow=false で isReadOnly=true になり編集できないため、
  // 案内を出すこと自体が誤誘導になる)。canEditNow は `canMutateTargetDate && !isCompleted`。
  const isAggregateLocked =
    aggregateSaveLocked ?? aggregateUnfilledRetailerNames.length > 0
  const showAggregateLockedHint =
    aggregateUnfilledRetailerNames.length > 0 && canEditNow

  // AC-DFD-001a (#10421): 当日断面で下書きが無い時の固定文言。「providerLabel 空」とは
  // 等価視しない (下書き有・手動入力のみの場合と区別する)。
  const noReceivedPlanLabel =
    !view.date.isReferenceView && view.source.isDraftMissing
      ? t('page:demandForecastDaily.provider.receivedPlanMissing')
      : undefined

  // 取込元サブラベル。BG 全体は配下の取込元が全て同じ時だけ出る。値変更を許さない
  // 時相での抑止と集計の判断は view.source (builder) に集約している。
  const sourceLabel = view.source.providerLabel ?? noReceivedPlanLabel

  // AC-DFD-047b/048a: 参照列「採用」ボタンの非活性理由キー。page-model の gate 導出値
  // (adjustmentDisabledReasonKey prop、Toolbar の「調整」ボタンと同一。優先: 確定済み >
  // 過去日 > 全コマ GC 済み > 小売未選択) を消費し、KomaCard 側で入力を再組立しない。
  // ただし集計スコープ (isAggregateScope) の採用は、過去実績・確定履歴とも
  // 各小売の値をそのまま入れる per-retailer 採用で、小売選択を要件にしない
  // (AC-DFD-047d / 048、issue #7436)。そのため「小売未選択 (noRetailer)」は理由にしない。
  const adoptDisabledReasonKey =
    isAggregateScope &&
    (adjustmentDisabledReasonKey === 'noRetailer' ||
      adjustmentDisabledReasonKey === 'unattributedRemaining')
      ? undefined
      : adjustmentDisabledReasonKey
  const adoptDisabledReason =
    adoptBlockedReason ??
    (adoptDisabledReasonKey !== undefined
      ? t(ADOPT_DISABLED_REASON_I18N_KEYS[adoptDisabledReasonKey])
      : undefined)

  return (
    <>
      {/* 48コマ表の列ヘッダー行はこのバーの直下に貼り付く。バーは保存状態の
          テキスト・競合時の追加ボタン・flexWrap で高さが変わるため、定数では
          なく実測値を KomaGrid へ渡す (models/komaTheadSticky の JSDoc 参照)。
          帯どうしの間に余白は空けない。 */}
      <Flex
        ref={headBarRef}
        position="sticky"
        zIndex={5}
        top={0}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        paddingBottom={2}
        background="Surface.primary"
      >
        <HStack gap={2}>
          {/* eslint-disable-next-line ebalance/no-adhoc-typography -- 既存コード (spec-align 差分外)。card title は md/semibold で sectionTitle (lg/semibold) とはスケールが異なるため既存の named role では代替できない。役割整理は別 issue */}
          <Text fontSize="md" fontWeight="semibold">
            {t('page:demandForecastDaily.koma.titleBgTotal')}
          </Text>
          {/* AC-01 (spec AC-DFC-011): 単位ラベルを見出し行内に常時表示する
              (design forecast-today.html L1135 `<span>単位：kWh</span>` 準拠) */}
          {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design L1135 の単位ラベルは sm/400、既存の named role では代替できない */}
          <Text color="Text.highEmphasis" fontSize="sm" fontWeight="normal">
            {t('page:demandForecastDaily.koma.unitLabel')}
          </Text>
        </HStack>
        <HStack gap={2}>
          <TertiaryButton
            disabled={!canDiscard}
            onClick={() => {
              setIsDiscardConfirmOpen(true)
            }}
            size="sm"
          >
            {t('page:demandForecastDaily.koma.discardButton')}
          </TertiaryButton>
          {currentSaveState === 'conflict' && (
            <TertiaryButton
              onClick={() => {
                setIsConflictReloadConfirmOpen(true)
              }}
              size="sm"
            >
              {t('page:demandForecastDaily.save.conflictReloadButton')}
            </TertiaryButton>
          )}
          {!isSaving &&
            currentSaveState !== 'error' &&
            currentSaveState !== 'conflict' &&
            lastSavedAt !== undefined && (
              <Text color="Text.positiveMid" fontSize="xs">
                {t('page:demandForecastDaily.save.lastSavedAt', {
                  time: formatMonthDayTime(lastSavedAt),
                })}
              </Text>
            )}
          {/* design dft-table-save 準拠: 48コマテーブルカードヘッダに保存ボタンを配置。
              過去日は保存を封鎖する (通常経路では dirty にならないが、当日ビュー
              編集中に JST 日跨ぎで過去日化したときの残存経路対策 =
              conventions.md §GC ロック / principles.md 鉄則1)。
              確定済み (readonly) 中は 破棄 / cell 編集と揃えて封鎖する
              (AC-DFC-007「全ての値変更操作」を無効化。保存経路単独 gate 抜けは
              PR #5226 ikdene review 指摘 = 計画に反映で dirty 化した後の保存も
              backend で reject されるため、UI 側でも disabled にして dead-end を防ぐ)。
              過去日封鎖 + 確定済み封鎖は page-model の canEditNow
              (`canMutateTargetDate && !isCompleted`) に集約済み。 */}
          {/* AC-DFD-004d: 未入力の内訳がある集計表示では保存も止める (理由は下の
              インフォメッセージに文で出す)。 */}
          <PrimaryButton
            disabled={!anyDirty || !canEditNow || isAggregateLocked}
            loading={isSaving}
            onClick={onSave}
            size="sm"
          >
            {t('page:demandForecastDaily.save.button')}
          </PrimaryButton>
        </HStack>
        {/* 保存エラーは操作ボタンと幅を奪い合わない独立した行に表示する。 */}
        {(currentSaveState === 'conflict' || currentSaveState === 'error') && (
          <Text width="full" color="Text.negativeMid" fontSize="xs">
            {currentSaveState === 'conflict'
              ? t('page:demandForecastDaily.save.conflictNotice')
              : (saveErrorMessage ?? t('page:demandForecastDaily.save.error'))}
          </Text>
        )}
      </Flex>

      {/* AC-DFD-004d: 直接編集不可 (BG 全体 + 配下複数 + 未完了) の理由を出す。
          description スロットの「小売電気事業者を選択して編集する」TextButton は
          撤去した — 配下が複数ある以上**遷移先が一意に定まらず**、押しても
          「どの小売に飛んだのか」が分からない導線だったため (issue #6302、
          2026-08-25 PdM 判断。spec AC-DFD-004d の ※)。
          対象切替は画面上部の BG/小売電気事業者/需要グループ切替に一本化する。 */}
      {isPreviewPending && (
        <Alert
          status="info"
          title={t('page:procurementPlanList.table.loading')}
        />
      )}
      {showAggregateLockedHint && (
        <Box marginBottom={2}>
          <CompactAlert
            description={t(
              'page:demandForecastDaily.koma.bgMultiRetailerReadOnlyAction',
            )}
            status="info"
            // 「編集できない」 だけだと**次に何をすればいいか**が読めない
            // (2026-08-25 デザイナー方針: アラートには何をしたら良いかも書く)。
            // spec AC-DFD-004d の括弧書き「配下の内訳を編集するには、 画面上部の
            // BG/小売電気事業者/需要グループ切替〈AC-DFC-001〉で対象を切り替える」
            // をそのまま案内文にする。 **リンク/ボタンにはしない** — 同 AC の ※ が
            // 遷移導線を禁じている (遷移先が一意に定まらないため)。
            title={t('page:demandForecastDaily.koma.aggregateUnfilledHint', {
              names: formatBreakdownNames(
                aggregateUnfilledRetailerNames,
                i18n.language,
              ),
              count: aggregateUnfilledRetailerNames.length,
            })}
          />
        </Box>
      )}

      {!showAggregateLockedHint &&
        (adjustmentDisabledReasonKey === 'groupTotal' ||
          adjustmentDisabledReasonKey === 'groupForecastBg') && (
          <Alert
            marginBottom={2}
            status="info"
            title={t(
              ADOPT_DISABLED_REASON_I18N_KEYS[adjustmentDisabledReasonKey],
            )}
          />
        )}

      {adjustmentDisabledReasonKey === 'unattributedRemaining' && (
        <CompactAlert
          status="warning"
          title={t(
            'page:demandForecastDaily.confirm.serverError.reason.unattributedRemaining',
          )}
        />
      )}

      {/* D8/D9 (slice 2): KomaGrid は同じ view インスタンスをそのまま受け取り
          gate 済み field (displaySlots / baselineSlots / todayActualSlots /
          manualEditKomas / dirtyKomas / gcKoma / currentKoma / referenceColumns /
          isReadOnly / isAggregateScope / adoptedReferenceColumnKey) を自身で view から destructure する。KomaCard はここで
          個別 prop へ展開し直さない。 */}
      <KomaGrid
        adoptDisabledReason={adoptDisabledReason}
        inputParser={inputParser}
        onAdoptReferenceColumn={onAdoptReferenceColumn}
        onChange={onKomaChange}
        onHoverKoma={onHoverKoma}
        onHoverSeriesKey={onHoverSeriesKey}
        onPaste={onPaste}
        onUndoAggregate={onUndoAggregate}
        sourceLabel={sourceLabel}
        stickyHeaderTop={headBarHeight}
        view={view}
      />

      <DiscardConfirmModal
        isOpen={isDiscardConfirmOpen}
        onCancel={() => {
          setIsDiscardConfirmOpen(false)
        }}
        onConfirm={() => {
          onDiscard?.()
          setIsDiscardConfirmOpen(false)
        }}
      />

      {/* AC-09: 「最新を読み込み直す」は未保存の変更を破棄するため確認を挟む */}
      <ConflictReloadConfirmDialog
        isOpen={isConflictReloadConfirmOpen}
        onCancel={() => {
          setIsConflictReloadConfirmOpen(false)
        }}
        onConfirm={() => {
          onResolveConflict?.()
          setIsConflictReloadConfirmOpen(false)
        }}
      />
    </>
  )
}
